import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null); // Track which sub-menu is expanded
  const [cmsServiceMenu, setCmsServiceMenu] = useState([]);
  const [cmsIndustryMenu, setCmsIndustryMenu] = useState([]);
  const location = useLocation();
  const closeTimeoutRef = useRef(null);

  // Handle menu open with delay cancellation
  const handleMenuEnter = (menuType) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenMenu(menuType);
  };

  // Handle menu close with delay
  const handleMenuLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
      setExpandedItem(null); // Reset expanded item when menu closes
    }, 500); // 500ms delay before closing - more time to navigate
  };

  // Handle hovering over a menu item - expand its sub-items
  const handleItemHover = (itemSlug) => {
    setExpandedItem(itemSlug);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      // Change header style as soon as user scrolls more than 50px
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch service menu from CMS
  useEffect(() => {
    const fetchServiceMenu = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setCmsServiceMenu(data);
        }
      } catch (error) {
        console.error('Failed to fetch service menu:', error);
      }
    };
    fetchServiceMenu();
  }, []);

  // Fetch industry menu from CMS
  useEffect(() => {
    const fetchIndustryMenu = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/industry-menu?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setCmsIndustryMenu(data);
        }
      } catch (error) {
        console.error('Failed to fetch industry menu:', error);
      }
    };
    fetchIndustryMenu();
  }, []);

  // Convert CMS menu to format expected by dropdown
  const cmsServicesData = cmsServiceMenu.length > 0 
    ? cmsServiceMenu.map(item => ({
        id: item.id,
        name: item.name,
        slug: item.slug,
        icon: item.icon || 'Code2',
        description: item.description,
        subservices: (item.sub_items || []).map(sub => ({
          id: sub.id,
          name: sub.name,
          slug: sub.slug,
          description: sub.description
        }))
      }))
    : [];

  // Convert CMS industry menu to format expected by dropdown (no sub-items - non-expandable)
  const cmsIndustriesData = cmsIndustryMenu.length > 0 
    ? cmsIndustryMenu.map(item => ({
        id: item.id,
        name: item.name,
        slug: item.slug,
        icon: item.icon || 'Building2',
        description: item.description,
        subcategories: [] // Industries menu is non-expandable - no sub-items shown
      }))
    : [];

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services', hasDropdown: true, menuType: 'services' },
    { name: 'Industries', path: '/industries', hasDropdown: true, menuType: 'industries' },
    { name: 'About', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' }
  ];

  const isHomePage = location.pathname === '/';
  const showWhiteStyle = isHomePage && !isScrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        showWhiteStyle ? 'bg-transparent' : 'bg-white shadow-md'
      }`}
    >
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - White on hero, colored after scroll */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_mobittech/artifacts/hyzx3sw9_Frame%202%20%281%29.png" 
              alt="Navaris Digital" 
              className={`h-10 w-auto transition-all duration-300 ${showWhiteStyle ? 'brightness-0 invert' : ''}`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => {
              if (item.hasDropdown) {
                const isMenuOpen = openMenu === item.menuType;
                // Use CMS data for services and industries
                const menuData = item.menuType === 'services' 
                  ? cmsServicesData 
                  : cmsIndustriesData;
                const basePath = item.menuType === 'services' ? '/services' : '/industries';
                
                return (
                  <div
                    key={item.path}
                    className="relative group"
                    onMouseEnter={() => handleMenuEnter(item.menuType)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <Link
                      to={basePath}
                      className={`px-4 py-2 rounded-lg text-base font-semibold transition-all flex items-center gap-1 ${
                        showWhiteStyle
                          ? location.pathname.startsWith(basePath)
                            ? 'text-white bg-white/20'
                            : 'text-white hover:bg-white/20'
                          : location.pathname.startsWith(basePath)
                            ? 'text-[#320547] bg-[#320547]/10'
                            : 'text-[#320547] hover:bg-[#320547]/10'
                      }`}
                    >
                      {item.name}
                      <ChevronDown size={16} className={`transition-transform duration-300 ${isMenuOpen ? 'rotate-180' : ''}`} />
                    </Link>

                    {/* Invisible bridge to prevent menu close when moving mouse */}
                    {isMenuOpen && (
                      <div className="absolute top-full left-0 w-full h-3 bg-transparent" />
                    )}

                    {/* Elegant Animated Dropdown */}
                    {isMenuOpen && menuData.length > 0 && (
                      <div 
                        className="absolute top-full left-0 pt-3 w-72 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                        onMouseEnter={() => handleMenuEnter(item.menuType)}
                        onMouseLeave={handleMenuLeave}
                      >
                        <div className="bg-white/95 backdrop-blur-xl rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-200/50 overflow-hidden">
                          {/* Menu Items */}
                          <div className="py-2">
                            {menuData.map((dataItem, index) => {
                              const itemPath = `${basePath}/${dataItem.slug}`;
                              const hasSubItems = dataItem.subservices || dataItem.subcategories;
                              const subItems = dataItem.subservices || dataItem.subcategories || [];
                              const isExpanded = expandedItem === dataItem.slug;
                              
                              return (
                                <div 
                                  key={dataItem.id} 
                                  style={{ animationDelay: `${index * 50}ms` }}
                                  onMouseEnter={() => handleItemHover(dataItem.slug)}
                                >
                                  {/* Main Category */}
                                  <Link
                                    to={itemPath}
                                    className={`flex items-center justify-between px-4 py-3 mx-2 rounded-lg transition-all duration-200 ${
                                      isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'
                                    }`}
                                    onClick={() => setOpenMenu(null)}
                                  >
                                    <span className={`font-semibold transition-colors duration-200 ${
                                      isExpanded ? 'text-[#320547]' : 'text-gray-800 hover:text-[#320547]'
                                    }`}>
                                      {dataItem.name}
                                    </span>
                                    <LucideIcons.ChevronRight 
                                      size={16} 
                                      className={`transition-all duration-200 ${
                                        isExpanded ? 'text-[#95228A] translate-x-1' : 'text-gray-300 hover:text-[#95228A]'
                                      }`}
                                    />
                                  </Link>
                                  
                                  {/* Sub Items - State controlled */}
                                  {hasSubItems && subItems.length > 0 && (
                                    <div className={`overflow-hidden transition-all duration-300 ease-out ${
                                      isExpanded ? 'max-h-[500px]' : 'max-h-0'
                                    }`}>
                                      <div className="mx-4 mb-2 py-2 pl-4 border-l-2 border-gray-200">
                                        {subItems.slice(0, 5).map((sub) => (
                                          <Link
                                            key={sub.id}
                                            to={`${basePath}/${dataItem.slug}/${sub.slug}`}
                                            className="block py-2 px-2 text-sm text-gray-500 hover:text-[#320547] hover:bg-gray-50 rounded transition-colors duration-150"
                                            onClick={() => setOpenMenu(null)}
                                          >
                                            {sub.name}
                                          </Link>
                                        ))}
                                        {subItems.length > 5 && (
                                          <Link
                                            to={itemPath}
                                            className="block py-2 px-2 text-sm font-medium text-[#95228A] hover:underline"
                                            onClick={() => setOpenMenu(null)}
                                          >
                                            View all {subItems.length} →
                                          </Link>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-base font-semibold transition-all ${
                    showWhiteStyle
                      ? location.pathname === item.path
                        ? 'text-white bg-white/20'
                        : 'text-white hover:bg-white/20'
                      : location.pathname === item.path
                        ? 'text-[#320547] bg-[#320547]/10'
                        : 'text-[#320547] hover:bg-[#320547]/10'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
            <Button 
              asChild 
              className={`ml-2 transition-all duration-300 ${
                showWhiteStyle 
                  ? 'bg-white text-[#320547] hover:bg-gray-100' 
                  : ''
              }`}
            >
              <Link to="/contact">Request Proposal</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-lg ${showWhiteStyle ? 'hover:bg-white/20' : 'hover:bg-gray-100'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={showWhiteStyle ? 'text-white' : 'text-[#320547]'} size={24} />
            ) : (
              <Menu className={showWhiteStyle ? 'text-white' : 'text-[#320547]'} size={24} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 bg-white rounded-xl shadow-lg max-h-[80vh] overflow-y-auto border border-gray-100">
            {navItems.map((item) => {
              if (item.hasDropdown) {
                const menuData = item.menuType === 'services' ? cmsServicesData : cmsIndustriesData;
                const basePath = item.menuType === 'services' ? '/services' : '/industries';
                
                return (
                  <div key={item.path}>
                    <Link
                      to={item.path}
                      className="block px-4 py-3 text-sm font-semibold text-[#320547] hover:bg-gray-50 border-b border-gray-100"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                    {menuData.map((dataItem) => {
                      const subItems = dataItem.subservices || dataItem.subcategories || [];
                      return (
                        <div key={dataItem.id} className="bg-gray-50">
                          <Link
                            to={`${basePath}/${dataItem.slug}`}
                            className="block px-6 py-2 text-sm font-medium text-gray-700 hover:text-[#320547]"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {dataItem.name}
                          </Link>
                          {subItems.slice(0, 3).map((sub) => (
                            <Link
                              key={sub.id}
                              to={`${basePath}/${dataItem.slug}/${sub.slug}`}
                              className="block px-8 py-1.5 text-xs text-gray-500 hover:text-[#320547]"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3 text-sm font-semibold transition-colors hover:bg-gray-50 ${
                    location.pathname === item.path
                      ? 'text-[#320547] bg-[#320547]/5'
                      : 'text-[#320547]'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              );
            })}
            <div className="px-4 py-3">
              <Button
                asChild
                className="w-full"
              >
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  Request Proposal
                </Link>
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
